import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import useUserComposable from '@/composables/UserComposable';

import DataSourceService from '../services/DataSourceService';
import AppResponse from '../../../../../helpers/app-response';

import { LogicalOperation, Procedure } from '../enums';
import { formatTableDate } from '../../../../../helpers/date-formatter';
import { generateUniqueKey } from '../../../../../helpers/text-formatter';

export const useDataSourceStore = defineStore('datasource', () => {
    const { company_id } = useUserComposable();
    // states
    const params = ref({
        company_id: null,
        page_size: null,
        page: null,
        paginate: null,
    });
    const dataSource = ref({
        company_id: null,
        name: null,
        code: null,
        description: null,
        sharable: null,
        links: [{ type: 'connector', logical_operation: LogicalOperation.OR, links: [{}] }],
    });
    const editDataSourceId = ref(null);
    const editDataSource = ref(dataSource.value);
    const editDataInvalid = ref(false);
    const dataSources = ref([]);
    const totalDataSources = ref(null);

    // getters
    const allDataSources = computed(() => {
        return dataSources.value?.map((dataSource) => {
            return {
                ...dataSource,
                created_at: formatTableDate(dataSource?.created_at),
                public: dataSource?.sharable === 1 ? true : false,
            };
        });
    });

    // actions

    // by default we want to add pagination, unless there is a paginate false in the params
    const fetchDataSources = async (paginate = true) => {
        const reqParams = {
            ...params.value,
            paginate,
            company_id: company_id.value,
        };
        const response = await DataSourceService.getAllDataSources(reqParams);
        if (paginate) {
            dataSources.value = response.data.data;
            totalDataSources.value = response.data.total;
        } else {
            dataSources.value = response.data;
        }
    };

    // For fetching existing datasource details by id
    const fetchDataSourceDetails = async (id) => {
        const response = await DataSourceService.getDataSourceDetails(id, company_id.value);
        if (response.statusCode === 200) {
            return response.data;
        }
    };

    const addDataSource = async () => {
        const bodyParams = {
            ...dataSource.value,
            sharable: dataSource.value.sharable ? true : false,
            company_id: company_id.value,
        };
        const response = await DataSourceService.addDataSource(bodyParams);
        if (response.statusCode === 201) {
            initDataSource();
            new AppResponse(response.statusCode, 'Data Source added successfully');
            const dataSourceId = response.data.id;
            return dataSourceId;
        }
    };

    const updateDataSource = async () => {
        const bodyParams = {
            ...dataSource.value,
            company_id: company_id.value,
        };

        // console.log('editDataSourceId.value', editDataSourceId.value);
        // console.log('dataSource.value id', dataSource.value);

        const response = await DataSourceService.updateDataSource(dataSource.value.id, bodyParams);
        if (response.statusCode === 200) {
            new AppResponse(response.statusCode, `Data Source ${editDataSourceId.value} updated successfully`);
            return true;
        }
    };

    const deleteDataSource = async (id) => {
        const response = await DataSourceService.deleteDataSource(id, company_id.value);
        if (response.statusCode === 200) {
            new AppResponse(response.statusCode, `Data Source ${id} deleted successfully`);
            await fetchDataSources();
        }
    };

    // helpers
    const initDataSource = async () => {
        dataSource.value.company_id = null;
        dataSource.value.name = null;
        dataSource.value.code = null;
        dataSource.value.description = null;
        dataSource.value.sharable = null;
        dataSource.value.links = [{ type: 'connector', logical_operation: LogicalOperation.OR, links: [{}] }];
        editDataSource.value = dataSource.value;
        editDataSourceId.value = null;
        editDataInvalid.value = false;
    };

    const setupDataSource = async (dataSource) => {
        // setup only required fields
        let links = await setupLinks(dataSource.links);
        editDataSourceId.value = dataSource.id;
        const dataSourceObj = {
            id: dataSource.id,
            company_id: dataSource.company_id,
            name: dataSource.name,
            code: dataSource.code,
            description: dataSource.description,
            isShared: dataSource.sharable === 1 ? true : false,
            links,
        };
        editDataSource.value = { ...dataSourceObj };
    };

    const setupLinks = (links) => {
        const updatedLinks = links.map((link) => {
            const displayClass = link.type === 'connector' ? { display: true } : null;
            return {
                ...displayClass,
                key: generateUniqueKey(), // this is for frontend only, to make use of unique key to push stuffs
                errors: { operatorError: false, valueError: false }, // this is for frontend only, for validations handling
                type: link.type,
                logical_operation: link.logical_operation,
                procedure: link.procedure,
                assessment_code: link.assessment_code,
                attribute_code: link.attribute_code,
                tag_id: link.tag_id,
                operator: link.operator,
                value: formatLinkValue(link),
                links: setupLinks(link.links),
            };
        });
        return updatedLinks;
    };

    const formatLinkValue = (link) => {
        const currentValue = link.value;
        const currentProcedure = link.procedure;
        if (!currentValue) {
            return null;
        }
        /**
         *  Check for cases for procedures
         *  When we get response from api, the each links have their value returned in form of string
         *  However, it needs to be considered from the front that we need to differentiate them
         *  In order to make sure we use the right property since we use values in many forms like
         *  Id in dropdowns, Ids of users
         *  OR
         *  It could also be a string that has a date value or assessment code or attribute code
         */
        switch (currentProcedure) {
            case Procedure.USER_TODAY_AGE:
            case Procedure.ASSESSMENT_USER_AGE:
            case Procedure.ATTRIBUTE:
            case Procedure.FIRST_ATTRIBUTE:
            case Procedure.LAST_ATTRIBUTE:
                return parseInt(currentValue);

            case Procedure.ASSESSMENT_DATE:
            case Procedure.ASSESSMENT_CODE:
            case Procedure.WITHOUT_ASSESSMENT_CODE:
            case Procedure.USER_LIST:
                return currentValue;

            case Procedure.PASS_FAIL: //different here onwards but same scenario or 1 or 0
            case Procedure.USER_PROFILE:
            case Procedure.ACTIVE_ONLY_ASSESSMENT:
            case Procedure.ACTIVE_ONLY_ATTRIBUTE:
                return parseInt(currentValue);
        }
    };

    /** Datasource dynamic nested operation addition starts */
    const addSelectedLinkType = (key, type) => {
        findKeyAndAddLink(editDataSource.value.links, key, type);
    };

    // find the key of the link and push new link information
    const findKeyAndAddLink = (links, key, newLinkType) => {
        links?.forEach((i) => {
            if (i.key === key) {
                const displayClass = i.type === 'connector' ? { display: true } : null;
                i.links.unshift({
                    ...displayClass,
                    key: generateUniqueKey(),
                    errors: { operatorError: false, valueError: false },
                    type: newLinkType,
                    logical_operation: null,
                    procedure: null,
                    assessment_code: null,
                    attribute_code: null,
                    tag_id: null,
                    operator: null,
                    value: null,
                    links: [],
                });
            } else {
                findKeyAndAddLink(i.links, key, newLinkType);
            }
        });
    };
    /** Datasource dynamic nested operation addition ends */

    // Toggle display starts
    const toggleDisplayInConnector = (key) => {
        findKeyAndToggle(editDataSource.value.links, key);
    };
    const findKeyAndToggle = (links, key) => {
        links?.forEach((i) => {
            if (i.key === key) {
                i.display = !i.display;
            } else {
                findKeyAndToggle(i.links, key);
            }
        });
    };
    // Toggle display ends

    // remove link key starts, this function can later be combined with findKeyAndAddLink
    const removeLinkWithKey = (linkKey) => {
        removeKeyInLinks(editDataSource.value.links, linkKey);
    };
    const removeKeyInLinks = (links, key) => {
        for (let i = 0; i < links.length; i++) {
            if (links[i].key === key) {
                links.splice(i, 1); // Remove the object at index i
                return;
            } else {
                removeKeyInLinks(links[i].links, key);
            }
        }
    };
    // remove link key ends

    // helper to check for validations before submission
    const isLinkInvalid = async () => {
        // check general details i.e name, code
        // check links validation
        await validateLinks();
        return editDataInvalid.value;
    };

    const validateLinks = () => {
        editDataInvalid.value = false;
        traverseEachLink(editDataSource.value.links);
        traverseEachLinkAndFindErrors(editDataSource.value.links);
    };

    // traverse each link and validate them
    const traverseEachLink = (links) => {
        links?.forEach((link) => {
            if (link && link.links ) {

                if (link?.links.length > 0) {
                    traverseEachLink(link?.links);
                }
                /**
                 * 1)  Validate connector
                 * Regarding this one, it is only for NOT operator to make sure it have only one link.
                 * we are ignoring this one since, it will handled within the DataSourceProLinks directly
                 * to avoid making any issues
                 */
                /**
                 * Validate procedure
                 * Regarding procedure validation, make sure to validate every procedures
                 * Attribute case will not be handled here at the moment since its not being done in form as well
                 * All Procedures have three main components most of the time except for attribute case
                 * Scenarios:-
                 * 1) Operator: Its mandatory for user_today_age, assessment_date, assessment_code,
                 * 2) Value: It is mandatory everywhere but we have to check one exception case for user_list where value
                 * needs to be of array type.
                 */

                if (link?.type === 'procedure') {
                    // Check if procedure is selected or not at first
                    link.errors = {
                        ...link.errors,
                        procedureError: !link.procedure,
                    };

                    // only check the inner procedure validation if a procedure is selected
                    if (link.procedure) {
                        // Scenario 1 checks
                        // we will use includes because there may be some additional procedures later as well
                        // Ignore in case of these procedure
                        if (![Procedure.PASS_FAIL, Procedure.WITHOUT_ASSESSMENT_CODE, Procedure.USER_PROFILE, Procedure.ACTIVE_ONLY_ASSESSMENT, Procedure.ACTIVE_ONLY_ATTRIBUTE, Procedure.USER_LIST].includes(link.procedure)) {
                            link.errors = {
                                ...link.errors,
                                operatorError: !link.operator ?? false, // what this is gonna do is add operatorError flag true
                            };
                        }
                        // Scenario 2 checks
                        // check if its a user_list type procedure first, if yes check for array orelse check for normal value
                        const isUserList = link.procedure === Procedure.USER_LIST;
                        const isBinaryValue = link.procedure === Procedure.PASS_FAIL || link.procedure === Procedure.USER_PROFILE || link.procedure === Procedure.ACTIVE_ONLY_ASSESSMENT || link.procedure === Procedure.ACTIVE_ONLY_ATTRIBUTE;
                        let isBinaryInvalid = true;
                        if (isBinaryValue) {
                            if (link.value === 1 || link.value === 0) {
                                isBinaryInvalid = false;
                            }
                        }
                        link.errors = {
                            ...link.errors,
                            valueError: isUserList ? !(link.value?.length > 0) : isBinaryValue ? isBinaryInvalid : !link.value,
                        };
                    }
                }
            }
        });
    };
    const traverseEachLinkAndFindErrors = (links) => {
        links.forEach((link) => {
            if (link.links) {
                traverseEachLinkAndFindErrors(link.links);
            }
            const error = link.errors;
            if (error && ( error.operatorError || error.procedureError || error.valueError)) {
                editDataInvalid.value = true;
            }
        });
    };
    // clean up extra information before submission

    const cleanLinks = (links) => {
        if (links) {
            return links.map((link) => {
                return {
                    type: link.type,
                    logical_operation: link.logical_operation,
                    procedure: link.procedure,
                    assessment_code: link.assessment_code,
                    attribute_code: link.attribute_code,
                    tag_id: link.tag_id,
                    operator: link.operator,
                    value: link.value,
                    links: cleanLinks(link.links),
                };
            });
        }
    };

    return {
        // states
        dataSource,
        editDataSource,
        editDataInvalid,
        dataSources,
        totalDataSources,
        // getters
        allDataSources,
        // helpers
        initDataSource,
        setupDataSource,
        addSelectedLinkType,
        toggleDisplayInConnector,
        removeLinkWithKey,
        isLinkInvalid,
        cleanLinks,
        // actions
        fetchDataSources,
        fetchDataSourceDetails,
        addDataSource,
        updateDataSource,
        deleteDataSource,
    };
});
